import { EntitySearchResult } from '@cbinsights/cbientityservice/cbientityservice';
import { Tag, Typography } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import {
  CountryHierarchyTree,
  buildLocationTree,
} from './CountryHierarchyTree';
import { DataFeedForm, JSONRequest } from './DataFeedForm';
import { formatMB } from './JSONRequestFilters';

const { Text, Title } = Typography;

export const EntityTypeStatus = {
  COMPANIES_AND_INVESTORS: 'Companies and investors',
  COMPANIES: 'Companies',
  INVESTORS: 'Investors',
} as const;

const renderTags = (items: DefaultOptionType[]) =>
  items.map((item) => (
    <Tag key={item.value} className="mb-1">
      {item.label}
    </Tag>
  ));

const renderSimpleTags = (items: string[]) =>
  items.map((item) => (
    <Tag key={item} className="mb-1">
      {item}
    </Tag>
  ));

const hasValidEntityValue = ({ value }: DefaultOptionType): boolean => {
  if (value == null || value === '') return false;
  try {
    JSON.parse(String(value));
    return true;
  } catch {
    return false;
  }
};

const renderEntityTags = (items: DefaultOptionType[]) =>
  items.filter(hasValidEntityValue).map(({ value }) => {
    const entity: EntitySearchResult = JSON.parse(String(value));
    return (
      <Tag key={value} className="mb-1">
        {`${entity.name} (${entity.idCbiEntity})`}
      </Tag>
    );
  });

const FilterValue = ({
  value,
  label,
}: {
  value: string | number | undefined;
  label?: string;
}) => {
  if (value == null || value === '') return null;
  if (label) {
    return (
      <div>
        <Text strong>{label}: </Text>
        <Tag>{value}</Tag>
      </div>
    );
  }
  return <Tag>{value}</Tag>;
};

type FilterSection = {
  title: string;
  content: React.ReactNode;
};

const renderSection = ({ title, content }: FilterSection) => {
  if (!content) return null;
  return (
    <div key={title}>
      <Title level={5}>{title}</Title>
      {content}
    </div>
  );
};

const tagsOrNull = (items: DefaultOptionType[] | undefined) =>
  items?.length ? renderTags(items) : null;

const simpleTagsOrNull = (items: string[] | undefined) =>
  items?.length ? renderSimpleTags(items) : null;

const entityTagsOrNull = (items: DefaultOptionType[] | undefined) =>
  items?.length ? renderEntityTags(items) : null;

const valueOrNull = (value: string | number | undefined) =>
  value != null && value !== '' ? <Tag>{value}</Tag> : null;

const CountryHierarchy = ({ formValues }: { formValues: DataFeedForm }) => {
  const treeData = buildLocationTree(formValues);
  if (!treeData?.length) return null;

  return (
    <div>
      <Title level={5}>Country Hierarchy</Title>
      <CountryHierarchyTree countryTreeData={treeData} />
    </div>
  );
};

const getVcBackedText = (
  vcBacked: DefaultOptionType | undefined
): string | undefined => {
  if (!vcBacked) return undefined;
  return vcBacked.value ? 'True' : 'False';
};

export const APIFiltersConfirmation = ({
  formValues,
}: {
  formValues: DataFeedForm;
}): React.ReactElement => {
  const filters = formValues?.filters[formValues.dataset];

  const filterSections: FilterSection[] = [
    { title: 'Organization IDs', content: entityTagsOrNull(filters?.orgIds) },
    {
      title: 'Organization Names',
      content: entityTagsOrNull(filters?.orgNames),
    },
    { title: 'URLs', content: simpleTagsOrNull(filters?.urls) },
    { title: 'Org Status', content: tagsOrNull(filters?.companyStatus) },
    {
      title: 'Descriptions',
      content: simpleTagsOrNull(filters?.descriptions),
    },
    {
      title: 'Entity Type',
      content: valueOrNull(String(filters?.entityType?.value ?? '')),
    },
    {
      title: 'Include only tier 1 orgs',
      content: filters?.tier1Only ? <Tag>Yes</Tag> : null,
    },
    { title: 'Sectors', content: tagsOrNull(filters?.sectors) },
    { title: 'Industries', content: tagsOrNull(filters?.industries) },
    { title: 'Sub-industries', content: tagsOrNull(filters?.subIndustries) },
    {
      title: 'Expert Collections',
      content: tagsOrNull(filters?.collectionIds),
    },
    {
      title: 'Funding Category',
      content: tagsOrNull(filters?.lastFundingRoundCategoryIds),
    },
    { title: 'Investor Types', content: tagsOrNull(filters?.investorTypeIds) },
    {
      title: 'VC Backed',
      content: valueOrNull(getVcBackedText(filters?.vcBacked)),
    },
    {
      title: 'Min Total Funding (USD in millions)',
      content: valueOrNull(filters?.minTotalFundingInMillions),
    },
    {
      title: 'Max Total Funding (USD in millions)',
      content: valueOrNull(filters?.maxTotalFundingInMillions),
    },
    {
      title: 'Min Deal Date',
      content: valueOrNull(filters?.minDealDate?.format('YYYY-MM-DD')),
    },
    {
      title: 'Max Deal Date',
      content: valueOrNull(filters?.maxDealDate?.format('YYYY-MM-DD')),
    },
    { title: 'Continents', content: tagsOrNull(filters?.continentIds) },
    { title: 'Regions', content: tagsOrNull(filters?.regionIds) },
    { title: 'Technologies', content: tagsOrNull(filters?.technologyIds) },
    {
      title: 'Business Models',
      content: tagsOrNull(filters?.businessModelIds),
    },
    {
      title: 'Awards & Spotlights',
      content: tagsOrNull(filters?.awardsAndSpotlightsIds),
    },
  ];

  return (
    <div className="space-y-4">
      {filterSections.map(renderSection)}
      <CountryHierarchy formValues={formValues} />
    </div>
  );
};

export const JSONRequestFiltersConfirmation = ({
  jsonRequest,
}: {
  jsonRequest: JSONRequest;
}) => (
  <div className="space-y-4">
    <Title level={5}>JSON request</Title>
    <Text>
      {jsonRequest.sourceName} — {formatMB(jsonRequest.size)}
    </Text>
  </div>
);

export const SearchConfigFiltersConfirmation = ({
  formValues,
}: {
  formValues: DataFeedForm;
}) => {
  const searchConfig = formValues?.filters?.searchConfig;
  if (!searchConfig?.tab || !searchConfig?.uuid) {
    return null;
  }
  return (
    <div className="space-y-4">
      <FilterValue label="Tab" value={String(searchConfig.tab.label)} />
      <FilterValue label="UUID" value={String(searchConfig.uuid)} />
      {searchConfig?.topNOrgs && (
        <FilterValue label="Top X orgs" value={searchConfig?.topNOrgs} />
      )}
    </div>
  );
};
